// CareReflection.tsx — the guided CARE exercise: Check-in, Anchoring, Reflection,
// Empowerment. Opens with a short introduction to the CARE approach, then the
// four steps. Saves a reflection into "Learning to calm triggered emotions" and
// records an empowering action as a soft reminder on Today.
import React from 'react';
import { Display, Body, Pressable } from '@/components/ui';
import { Icon } from '@/components/icons';
import { useProgress } from '@/hooks/useProgress';
import { IN_APP_JOURNALING } from '@/config/features';
import { BODY_QUESTIONS, EMPOWERMENT_EXAMPLES, CARE_SECTION, CARE_PROMPT_TITLE, CARE_TAGLINE } from '@/data/care';

type StepKey = 'check' | 'anchor' | 'reflect' | 'empower';
const STEPS: { key: StepKey; letter: string; name: string }[] = [
  { key: 'check', letter: 'C', name: 'Check-in' },
  { key: 'anchor', letter: 'A', name: 'Anchoring' },
  { key: 'reflect', letter: 'R', name: 'Reflection' },
  { key: 'empower', letter: 'E', name: 'Empowerment' },
];

const fieldStyle: React.CSSProperties = {
  width: '100%',
  minHeight: 104,
  resize: 'vertical',
  borderRadius: 14,
  border: '1px solid var(--hair)',
  background: 'var(--card-top)',
  padding: '13px 14px',
  fontFamily: 'Fraunces, Georgia, serif',
  fontSize: 16,
  lineHeight: 1.55,
  color: 'var(--ink)',
  WebkitUserSelect: 'text',
  userSelect: 'text',
};

const promptStyle: React.CSSProperties = {
  fontFamily: 'Inter, system-ui, sans-serif',
  fontSize: 13.5,
  fontStyle: 'italic',
  lineHeight: 1.5,
  color: 'var(--amber-deep)',
  margin: '0 0 9px',
};

// A Yes / No pair for the Reflection step's questions (same look as the Anchoring choices).
function YesNo({ value, onChange }: { value: boolean | null; onChange: (v: boolean) => void }) {
  return (
    <div style={{ display: 'flex', gap: 9 }}>
      {[
        { v: true, label: 'Yes' },
        { v: false, label: 'No' },
      ].map((o) => (
        <Pressable
          key={o.label}
          as="button"
          onClick={() => onChange(o.v)}
          ariaLabel={o.label}
          style={{
            flex: 1,
            padding: '13px 15px',
            borderRadius: 13,
            border: value === o.v ? '1.5px solid var(--amber-deep)' : '1px solid var(--hair)',
            background: value === o.v ? 'rgba(200,134,26,0.08)' : 'var(--card-top)',
            color: 'var(--ink)',
            cursor: 'pointer',
            fontFamily: 'Inter, system-ui, sans-serif',
            fontSize: 14.5,
            fontWeight: 600,
          }}
        >
          {o.label}
        </Pressable>
      ))}
    </div>
  );
}

// Shown in place of a text box when the user journals off-app: a gentle nudge to
// write in their own journal. Their words are never entered into, or stored by, the app.
function JournalCue() {
  return (
    <div style={{ display: 'flex', gap: 11, padding: '13px 15px', borderRadius: 14, border: '1px solid rgba(29,158,117,0.18)', background: 'rgba(29,158,117,0.05)' }}>
      <span style={{ color: 'var(--green-deep)', flexShrink: 0, marginTop: 1 }}>
        <Icon name="book" size={18} />
      </span>
      <Body dim size={13.5}>Take a moment to write this in your own journal, in your own time.</Body>
    </div>
  );
}

export function CareReflection({ onClose, onOpenCrisis }: { onClose: () => void; onOpenCrisis: () => void }) {
  const progress = useProgress();
  const inApp = IN_APP_JOURNALING && progress.state.inAppJournaling;
  const [inIntro, setInIntro] = React.useState(true);
  const [stepIdx, setStepIdx] = React.useState(0);
  const [stirred, setStirred] = React.useState<boolean | null>(null);
  const [realThreat, setRealThreat] = React.useState<boolean | null>(null);
  const [triggered, setTriggered] = React.useState<boolean | null>(null);
  const [reflectSelf, setReflectSelf] = React.useState('');
  const [reflectReframe, setReflectReframe] = React.useState('');
  const [empowerText, setEmpowerText] = React.useState('');

  const step = STEPS[stepIdx];
  const isLast = stepIdx === STEPS.length - 1;
  // the Empowerment step needs an answer before saving — but only when the user
  // journals in the app; off-app, they write in their own journal so we don't gate.
  const canContinue = inIntro || step.key !== 'empower' || !inApp || empowerText.trim().length > 0;

  // Closing out (from the intro, before any step) still counts as "dealt with" for
  // the "When you're ready" prompt — no matter which entry point opened CARE (the
  // triage button, the waiting card, or anywhere else), the flag must not survive
  // a close that isn't a full finish(), or the card would nag forever after a
  // user who engaged with CARE but didn't fill in every step.
  const goBack = () => {
    if (inIntro) { progress.dismissCare(); onClose(); }
    else if (stepIdx === 0) setInIntro(true);
    else setStepIdx((i) => i - 1);
  };

  const finish = () => {
    const body = [
      reflectSelf.trim() ? `What my trauma is telling me to do: ${reflectSelf.trim()}` : '',
      reflectReframe.trim() ? `What I would tell a loved one to do: ${reflectReframe.trim()}` : '',
      empowerText.trim() ? `One empowering thing I will do: ${empowerText.trim()}` : '',
    ]
      .filter(Boolean)
      .join('\n\n');
    if (body) {
      progress.saveReflection({
        stepId: `care-${new Date().toISOString()}`,
        module: CARE_SECTION,
        prompt: CARE_PROMPT_TITLE,
        body,
      });
    }
    if (empowerText.trim()) progress.addCareAction(empowerText);
    else progress.dismissCare();
    onClose();
  };

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 84,
        background: 'var(--cream)',
        display: 'flex',
        flexDirection: 'column',
        animation: 'rise .42s cubic-bezier(.2,.7,.3,1)',
      }}
    >
      <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <div style={{ maxWidth: 680, margin: '0 auto', padding: 'calc(18px + var(--safe-top)) 22px calc(40px + var(--safe-bottom))' }}>
          {/* header: back + (in steps) CARE step pips */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 16 }}>
            <button
              type="button"
              onClick={goBack}
              aria-label="Back"
              style={{
                width: 40,
                height: 40,
                borderRadius: 999,
                border: '1px solid var(--hair)',
                background: 'linear-gradient(180deg, var(--card-top), var(--card-bot))',
                color: 'var(--ink)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: 'var(--edge), var(--sh1)',
              }}
            >
              <Icon name="chevLeft" size={18} />
            </button>
            {!inIntro && (
              <div style={{ display: 'flex', gap: 6 }}>
                {STEPS.map((s, i) => (
                  <span
                    key={s.key}
                    aria-hidden="true"
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: 999,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontFamily: 'Fraunces, Georgia, serif',
                      fontSize: 13,
                      fontWeight: 600,
                      background: i <= stepIdx ? 'var(--amber-deep)' : 'transparent',
                      color: i <= stepIdx ? '#FBF1DE' : 'var(--ink-soft)',
                      border: i <= stepIdx ? 'none' : '1px solid var(--hair)',
                    }}
                  >
                    {s.letter}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* ── intro to the CARE approach ── */}
          {inIntro && (
            <>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, color: 'var(--amber-deep)', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>
                <Icon name="sparkle" size={14} /> Recommended action
              </div>
              <Display parts={{ pre: 'The ', em: 'CARE', post: ' approach' }} size={27} lh={1.15} />
              <Body size={15} style={{ margin: '13px 0 14px' }}>
                {CARE_TAGLINE} It has four small steps: Check-in, Anchoring, Reflection, and Empowerment. We
                recommend coming back to it whenever you have experienced triggered emotions. There is no rush,
                so return to it whenever you have the time and space.
              </Body>
              <Body dim size={13.5} style={{ margin: '0 0 14px' }}>
                Dr Craig Newman developed CARE as part of his recovery model for relationship trauma. He uses it
                regularly in his therapy practice, where he has seen great benefit from it.
              </Body>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 4 }}>
                {STEPS.map((s) => (
                  <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                    <span style={{ width: 28, height: 28, borderRadius: 999, background: 'rgba(200,134,26,0.12)', color: 'var(--amber-deep)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Fraunces, Georgia, serif', fontSize: 14, fontWeight: 600, flexShrink: 0 }}>
                      {s.letter}
                    </span>
                    <span style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14.5, color: 'var(--ink)' }}>{s.name}</span>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* eyebrow for the active step */}
          {!inIntro && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, color: 'var(--amber-deep)', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>
              CARE · {step.letter} · {step.name}
            </div>
          )}

          {!inIntro && step.key === 'check' && (
            <>
              <Display parts={{ pre: 'Check in with your ', em: 'body' }} size={26} lh={1.16} />
              <Body size={15} style={{ margin: '12px 0 16px' }}>
                Not your thoughts, and not what it all means. Just what your body was doing. When trauma fires,
                the body is far louder than thinking, and we can rarely think clearly. So take a moment to simply
                notice what your body was telling you. For example, gently ask yourself:
              </Body>
              <div style={{ borderRadius: 16, border: '1px solid var(--hair)', background: 'var(--card-top)', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {BODY_QUESTIONS.map((q) => (
                  <div key={q} style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                    <span aria-hidden="true" style={{ width: 5, height: 5, borderRadius: 999, background: 'var(--green-deep)', flexShrink: 0, transform: 'translateY(-3px)' }} />
                    <span style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: 16.5, fontStyle: 'italic', fontWeight: 300, lineHeight: 1.4, color: 'var(--ink)' }}>{q}</span>
                  </div>
                ))}
              </div>
              <Body dim size={13} style={{ marginTop: 13 }}>
                There is nothing to fill in here. Just listen, and let your body be heard.
              </Body>
            </>
          )}

          {!inIntro && step.key === 'anchor' && (
            <>
              <Display parts={{ pre: 'Anchor your ', em: 'body' }} size={26} lh={1.16} />
              <Body size={15} style={{ margin: '12px 0 16px' }}>
                Trying to remember what your body was feeling can sometimes stir those feelings up again. Notice
                what is happening for you now. Is this a feeling rising in your body again, or does it feel more
                like a memory? You may also still be carrying these feelings, because triggered emotions do not
                always pass quickly.
              </Body>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginBottom: 16 }}>
                {[
                  { v: true, label: 'Yes, my body is still feeling disturbed' },
                  { v: false, label: 'No, I feel settled' },
                ].map((o) => (
                  <Pressable
                    key={o.label}
                    as="button"
                    onClick={() => setStirred(o.v)}
                    ariaLabel={o.label}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      padding: '13px 15px',
                      borderRadius: 13,
                      border: stirred === o.v ? '1.5px solid var(--amber-deep)' : '1px solid var(--hair)',
                      background: stirred === o.v ? 'rgba(200,134,26,0.08)' : 'var(--card-top)',
                      color: 'var(--ink)',
                      cursor: 'pointer',
                      fontFamily: 'Inter, system-ui, sans-serif',
                      fontSize: 14.5,
                      fontWeight: 600,
                    }}
                  >
                    {o.label}
                  </Pressable>
                ))}
              </div>
              {stirred === true && (
                <>
                  <div style={{ borderRadius: 16, border: '1px solid rgba(29,158,117,0.35)', background: 'linear-gradient(180deg, rgba(29,158,117,0.09), rgba(29,158,117,0.04))', padding: '15px 16px' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, color: 'var(--green-deep)', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 9 }}>
                      <Icon name="leaf" size={14} /> A physiological sigh
                    </div>
                    <Body size={15}>
                      Breathe in slowly through your nose. Then sip in a little more air to fill your lungs to the
                      top. Now let it go in one long, slow breath out through your mouth. Repeat three to five times,
                      until your body softens a little.
                    </Body>
                  </div>
                  <Body dim size={13} style={{ marginTop: 13 }}>
                    If your body and feelings are very hard to settle and it all feels overwhelming, it is better
                    not to carry on with this exercise right now. Give yourself time, and perhaps return to Calm
                    Your Body for a longer breathing exercise. It is okay to wait. For many people this takes a
                    long time, and it is not wise to work through the next steps while your body is telling you it
                    feels very off balance.
                  </Body>
                </>
              )}
            </>
          )}

          {!inIntro && step.key === 'reflect' && (
            <>
              <Display parts={{ pre: 'What actually ', em: 'happened' }} size={26} lh={1.16} />

              {/* 1. Real threat? Yes → seek help now; No → on to the trauma-reaction question. */}
              <p style={{ ...promptStyle, marginTop: 14 }}>
                Did something happen that justifies your body activating itself to fight or run for survival?
              </p>
              <YesNo value={realThreat} onChange={(v) => { setRealThreat(v); setTriggered(null); }} />

              {realThreat === true && (
                <div style={{ marginTop: 14, borderRadius: 16, border: '1px solid rgba(196,99,76,0.4)', background: 'rgba(196,99,76,0.06)', padding: '15px 16px' }}>
                  <Body size={15}>
                    If you have experienced a real threat or risk, please seek help now.
                  </Body>
                  <button
                    type="button"
                    onClick={onOpenCrisis}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 9,
                      width: '100%',
                      marginTop: 12,
                      minHeight: 48,
                      padding: '12px 14px',
                      borderRadius: 13,
                      border: 'none',
                      background: '#9c3d2c',
                      color: '#fff',
                      cursor: 'pointer',
                      fontFamily: 'Inter, system-ui, sans-serif',
                      fontSize: 15,
                      fontWeight: 600,
                    }}
                  >
                    <Icon name="shield" size={16} />
                    Get help now
                  </button>
                </div>
              )}

              {/* 2. Triggered trauma reaction? */}
              {realThreat === false && (
                <>
                  <p style={{ ...promptStyle, marginTop: 20 }}>
                    Then why has your body reacted this way? Might it be a triggered trauma reaction?
                  </p>
                  <Body dim size={13.5} style={{ margin: '0 0 10px' }}>
                    Triggered trauma is an over-reaction by your body to something that reminded it of past trauma.
                  </Body>
                  <YesNo value={triggered} onChange={setTriggered} />
                </>
              )}

              {/* 3 + 4. What the trauma is urging, and what you'd tell a loved one to do. */}
              {realThreat === false && triggered !== null && (
                <>
                  {triggered && (
                    <>
                      <p style={{ ...promptStyle, marginTop: 20 }}>
                        What is your trauma telling you to do? For example, hide, blame yourself, feel guilty, or
                        strike out.
                      </p>
                      {inApp ? (
                        <textarea value={reflectSelf} onChange={(e) => setReflectSelf(e.target.value)} placeholder="Write what it is urging…" style={fieldStyle} />
                      ) : (
                        <JournalCue />
                      )}
                    </>
                  )}

                  <p style={{ ...promptStyle, marginTop: 20 }}>
                    What would you recommend a loved one (perhaps your friend, sibling or child) to do, if they were
                    you and facing the same situation? For example, journal, distract yourself, remind yourself that
                    you are safe and valuable, take a walk, take a shower, or create something artistic.
                  </p>
                  {inApp ? (
                    <textarea value={reflectReframe} onChange={(e) => setReflectReframe(e.target.value)} placeholder="Write what you would tell them…" style={fieldStyle} />
                  ) : (
                    <JournalCue />
                  )}
                </>
              )}
            </>
          )}

          {!inIntro && step.key === 'empower' && (
            <>
              <Display parts={{ pre: 'One empowering ', em: 'thing' }} size={26} lh={1.16} />
              <Body size={15} style={{ margin: '12px 0 14px' }}>
                Empowerment is an act. It is something the trauma part of you is not telling you to do, but that you
                would tell someone else to do if they were feeling overwhelmed by their past trauma. Think about what
                you would advise them, and then do it for yourself. Small is good.
              </Body>
              <ul style={{ margin: '0 0 14px', paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 5 }}>
                {EMPOWERMENT_EXAMPLES.map((ex, i) => (
                  <li key={i} style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13.5, lineHeight: 1.5, color: 'var(--ink-soft)' }}>{ex}</li>
                ))}
              </ul>
              {inApp ? (
                <textarea value={empowerText} onChange={(e) => setEmpowerText(e.target.value)} placeholder="What will you do?" style={{ ...fieldStyle, minHeight: 84 }} />
              ) : (
                <JournalCue />
              )}
              {!canContinue && (
                <Body dim size={12.5} style={{ marginTop: 9 }}>
                  Add one small thing for yourself to finish.
                </Body>
              )}
            </>
          )}

          {/* footer action */}
          <div style={{ marginTop: 22 }}>
            <Pressable
              as="button"
              disabled={!canContinue}
              onClick={() => {
                if (inIntro) setInIntro(false);
                else if (isLast) finish();
                else setStepIdx((i) => i + 1);
              }}
              ariaLabel={inIntro ? 'Continue' : isLast ? 'Save and finish' : 'Continue'}
              style={{
                width: '100%',
                padding: '15px 16px',
                borderRadius: 15,
                border: 'none',
                background: canContinue ? 'var(--green-deep)' : 'var(--hair)',
                color: canContinue ? '#fff' : 'var(--ink-soft)',
                cursor: canContinue ? 'pointer' : 'default',
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: 15,
                fontWeight: 600,
              }}
            >
              {inIntro ? 'Continue' : isLast ? 'Save & finish' : 'Continue'}
            </Pressable>
          </div>
        </div>
      </div>
    </div>
  );
}
